<template>
    <slot name="actions" :on-add-address="onAddAddress" />

    <div class="px-4 lg:p-0 pb-6">
        <div class="lg:border lg:border-secondary lg:rounded-xs lg:p-6 lg:mb-6 lg:max-w-3xl">
            <div class="font-semibold mb-4">
                {{ $t('customer.addressBook.defaultAddresses') }}
            </div>
            <div class="grid grid-cols-2 gap-6 mb-6 lg:mb-0">
                <template v-if="(loading && addresses == null) || loadingUpdate">
                    <div class="col-span-2 md:col-span-1 grid gap-2">
                        <MiscSkeleton size="medium" :repeat="6" />
                    </div>
                    <div class="col-span-2 md:col-span-1 grid gap-2">
                        <MiscSkeleton size="medium" :repeat="6" />
                    </div>
                </template>
                <div
                    v-for="address in filterDefault(addresses)"
                    v-else-if="addresses != null"
                    :key="address.id"
                    data-cy="default-address-wrapper"
                    class="relative col-span-2 md:col-span-1 border border-secondary rounded-xs p-6 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1)]"
                    :class="{ 'order-1': address.id === defaultBillingAddressId }"
                >
                    <div class="badge badge-primary rounded-xs px-3 mb-4">
                        {{ $t('customer.addressBook.default') }} {{ address.id === defaultBillingAddressId ? $t('customer.addressBook.billing') : '' }} {{ address.id === defaultShippingAddressId ? $t('customer.addressBook.shipping') : '' }}
                    </div>
                    <CustomerAddressRenderer :address="address" />
                    <div class="flex space-x-2 mt-6">
                        <button 
                            type="button" 
                            class="btn btn-secondary btn-sm" 
                            data-cy="default-address-edit" 
                            @click="onEditAddress(address.id)"
                        >
                            {{ $t('customer.addressBook.edit') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="lg:border lg:border-secondary lg:rounded-xs lg:p-6 lg:mb-6 lg:max-w-3xl">
            <div class="font-semibold mb-4">
                {{ $t('customer.addressBook.additionalAddresses') }}
            </div>
            <div class="grid grid-cols-2 gap-6 mb-6 lg:mb-0">
                <template v-if="(loading && addresses == null) || loadingUpdate">
                    <div class="col-span-2 md:col-span-1 grid gap-2">
                        <MiscSkeleton size="medium" :repeat="6" />
                    </div>
                    <div class="col-span-2 md:col-span-1 grid gap-2">
                        <MiscSkeleton size="medium" :repeat="6" />
                    </div>
                </template>
                <div
                    v-for="address in filterNotDefault(addresses)"
                    v-else-if="addresses != null"
                    :key="address.id"
                    class="relative col-span-2 md:col-span-1 border border-secondary rounded-xs p-6 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1)]"
                >
                    <CustomerAddressRenderer :address="address" />
                    <div class="mt-3">
                        <div
                            role="button"
                            class="link link-primary"
                            tabindex="0"
                            @click="setDefault('shipping', address.id)"
                            @keyup.enter="setDefault('shipping', address.id)"
                        >
                            {{ $t('customer.addressBook.default') }} {{ $t('customer.addressBook.shipping') }}
                        </div>

                        <div
                            role="button"
                            class="link link-primary"
                            tabindex="0"
                            @click="setDefault('billing', address.id)"
                            @keyup.enter="setDefault('billing', address.id)"
                        >
                            {{ $t('customer.addressBook.default') }} {{ $t('customer.addressBook.billing') }}
                        </div>
                    </div>
                    <div class="flex space-x-2 mt-6">
                        <button type="button" class="btn btn-secondary btn-sm" @click="onEditAddress(address.id)">
                            {{ $t('customer.addressBook.edit') }}
                        </button>
                        <button class="btn btn-ghost btn-sm" @click="onDeleteAddress(address.id)">
                            {{ $t('customer.addressBook.delete') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <input
        id="address-form-modal"
        v-model="modalState"
        type="checkbox"
        class="modal-toggle"
    >
    <div class="modal modal-top sm:modal-middle">
        <div class="modal-box relative max-h-screen">
            <label
                role="button"
                tabindex="0"
                for="address-form-modal"
                class="btn btn-sm btn-circle absolute right-2 top-2"
                @click.prevent="modalState = false"
                @keydown.enter="modalState = false"
            >
                <MiscIcon name="x" class="h-4 w-4" />
                <span class="hidden">{{ $t('customer.close') }}</span>
            </label>
            <div class="text-2xl mb-2">
                {{ $t(`customer.addressBook.action${formAction}`) }}
            </div>
            <form ref="addressBookForm" class="w-full relative flex flex-col" @submit.prevent="onFormSubmit()">
                <CustomerAddressForm
                    v-if="formAction === 'Add' || formAction === 'Edit'"
                    :model-value="formData"
                    type="shipping"
                    @update:model-value="onUpdateAddress($event)"
                />
                <CustomerAddressRenderer v-if="formAction === 'Delete' && formData.id" :address="(formData as Schemas['CustomerAddress'])" />
                <div v-if="formAction === 'Add' || formAction === 'Edit'" class="form-control mt-4">
                    <label for="useDefaultBilling" class="label justify-start cursor-pointer">
                        <input
                            id="useDefaultBilling"
                            v-model="useAsDefaultBilling"
                            type="checkbox"
                            class="checkbox mr-2"
                        >
                        <span class="label-text">{{ $t('customer.addressBook.defaultBillingAction') }}</span>
                    </label>
                </div>
                <div v-if="formAction === 'Add' || formAction === 'Edit'" class="form-control">
                    <label for="useDefaultShipping" class="label justify-start cursor-pointer">
                        <input
                            id="useDefaultShipping"
                            v-model="useAsDefaultShipping"
                            type="checkbox"
                            class="checkbox mr-2"
                        >
                        <span class="label-text">{{ $t('customer.addressBook.defaultShippingAction') }}</span>
                    </label>
                </div>
                <button
                    type="submit"
                    :disabled="loading"
                    class="btn btn-primary text-right mt-4"
                    data-cy="customer-address-modal-submit"
                    @click.prevent="onFormSubmit()"
                >
                    <span v-if="loading" class="loading" />
                    {{ $t(`customer.addressBook.action${formAction}`) }}
                </button>
            </form>
        </div>
        <label tabindex="0" class="modal-backdrop cursor-pointer" for="address-form-modal">{{ $t('customer.close') }}</label>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { stripNullish } from '~/utils/stripNullish'

/*
 * Fetch Addresses
 */
const {
    loadCustomerAddresses,
    createCustomerAddress,
    updateCustomerAddress,
    deleteCustomerAddress,
    setDefaultCustomerBillingAddress,
    setDefaultCustomerShippingAddress,
} = useAddress()
const { defaultBillingAddressId, defaultShippingAddressId, refreshUser } = useUser()
const { showNotification } = useNotification()

const loading = ref(false)
const loadingUpdate = ref(false)
const addresses: Ref<null | Schemas['CustomerAddress'][]> = ref(null)

onMounted(async () => {
    loading.value = true
    addresses.value = await loadCustomerAddresses()
    loading.value = false
})

/*
 * Handle address actions
 */
const modalState: Ref<boolean> = ref(false)
const formData: Ref<Schemas['CustomerAddress'] | Record<string, never>> = ref({})
const formAction: Ref<null | 'Add' | 'Edit' | 'Delete'> = ref(null)
const addressBookForm = ref<HTMLFormElement>()
const useAsDefaultBilling = ref(false)
const useAsDefaultShipping = ref(false)

function onAddAddress () {
    formAction.value = 'Add'
    formData.value = {}
    modalState.value = true
}

function onEditAddress (id: string) {
    formAction.value = 'Edit'
    formData.value = Object.assign({}, addresses?.value?.find(address => address.id === id))
    modalState.value = true
}

function onDeleteAddress (id: string) {
    formAction.value = 'Delete'
    formData.value = Object.assign({}, addresses?.value?.find(address => address.id === id))
    modalState.value = true
}

function onUpdateAddress (data: Schemas['CustomerAddress']) {
    formData.value = data
}

async function onFormSubmit () {
    if (formAction.value !== 'Delete' && !addressBookForm.value?.checkValidity()) {
        addressBookForm.value?.reportValidity()
        return
    }

    loading.value = true
    let response: Schemas['CustomerAddress'] | null = null

    try {
        if (formAction.value === 'Edit') {
            response = await updateCustomerAddress(stripNullish(formData.value as Schemas['CustomerAddress']))
        }

        if (formAction.value === 'Add') {
            response = await createCustomerAddress(formData.value as Schemas['CustomerAddress'])
        }

        if (formAction.value === 'Delete') {
            await deleteCustomerAddress((formData.value as Schemas['CustomerAddress']).id as string)
        }

        if (useAsDefaultBilling.value && response != null) {
            await setDefaultCustomerBillingAddress(response.id as string)
        }

        if (useAsDefaultShipping.value && response != null) {
            await setDefaultCustomerShippingAddress(response.id as string)
        }

        modalState.value = false
        addresses.value = await loadCustomerAddresses()
        await refreshUser()
    } catch (e) {
        showNotification(e instanceof Error ? e.message : 'An error occurred', 'error', true)
    } finally {
        loading.value = false
    }
}

function filterDefault (addresses: Schemas['CustomerAddress'][]) {
    return addresses.filter((address) => {
        return address.id === defaultBillingAddressId.value || address.id === defaultShippingAddressId.value
    })
}

function filterNotDefault (addresses: Schemas['CustomerAddress'][]) {
    return addresses.filter((address) => {
        return address.id !== defaultBillingAddressId.value && address.id !== defaultShippingAddressId.value
    })
}

async function setDefault (type: 'shipping' | 'billing', id: string) {
    loadingUpdate.value = true
    try {
        if (type === 'billing') {
            await setDefaultCustomerBillingAddress(id)
        } else if (type === 'shipping') {
            await setDefaultCustomerShippingAddress(id)
        } else {
            return
        }

        addresses.value = await loadCustomerAddresses()
        await refreshUser()
    } catch (e) {
        console.error(e)
    } finally {
        loadingUpdate.value = false
    }
}

watch(modalState, (value) => {
    if (!value) {
        formAction.value = null
        useAsDefaultBilling.value = false
        useAsDefaultShipping.value = false
        formData.value = {}
    }
})
</script>
